import React from 'react';
import Seat from './Seat';
import toiletIcon from '../../img/toilet_icon.svg';

// Seat status constants (no external dependency)
var SEAT_STATUS = {
  AVAILABLE: 'available',
  TAKEN: 'taken',
  TEMPORARY_TAKEN: 'temporary_taken',
  SELECTED: 'selected',
  FIRST_COME_FIRST_SERVE: 'first_come_first_serve'
};

class SeatMap extends React.Component {
  /**
   * Generate rows dynamically from backend busLayout
   */
  generateRows() {
    var busLayout = this.props.busLayout;

    if (!busLayout || !busLayout.columns || !busLayout.totalRows) {
      return [];
    }

    var totalRows = busLayout.totalRows;
    var seatsPerRowLeft = busLayout.seatsPerRowLeft;
    var seatsPerRowRight = busLayout.seatsPerRowRight;
    var columns = busLayout.columns;
    var specialCells = busLayout.specialCells || {};
    var rows = [];

    var leftColumns = columns.slice(0, seatsPerRowLeft);
    var rightColumns = columns.slice(seatsPerRowLeft, seatsPerRowLeft + seatsPerRowRight);

    for (var i = 1; i <= totalRows; i++) {
      var rowNum = i;
      var leftCells = leftColumns.map(function(col) {
        var cellId = String(rowNum) + col;
        var spec = specialCells[cellId];
        return { id: cellId, type: spec ? spec.type : 'seat' };
      });
      var rightCells = rightColumns.map(function(col) {
        var cellId = String(rowNum) + col;
        var spec = specialCells[cellId];
        return { id: cellId, type: spec ? spec.type : 'seat' };
      });
      rows.push({
        rowNumber: rowNum,
        leftCells: leftCells,
        rightCells: rightCells
      });
    }

    return rows;
  }

  getSeatStatus(seatId) {
    var maxSelectableRow = this.props.maxSelectableRow;
    var selectedSeats = this.props.selectedSeats || [];
    var takenSeats = this.props.takenSeats || [];
    var temporaryTakenSeats = this.props.temporaryTakenSeats || [];

    // Check if seat falls in a first-come-first-served row
    if (maxSelectableRow != null) {
      var rowMatch = seatId.match(/^(\d+)/);
      if (rowMatch && parseInt(rowMatch[1], 10) > maxSelectableRow) {
        return SEAT_STATUS.FIRST_COME_FIRST_SERVE;
      }
    }

    if (selectedSeats.indexOf(seatId) !== -1) {
      return SEAT_STATUS.SELECTED;
    }
    if (takenSeats.indexOf(seatId) !== -1) {
      return SEAT_STATUS.TAKEN;
    }
    if (temporaryTakenSeats.indexOf(seatId) !== -1) {
      return SEAT_STATUS.TEMPORARY_TAKEN;
    }
    return SEAT_STATUS.AVAILABLE;
  }

  // Get addon price for a seat using backend pricing tiers
  getSeatPrice(seatId) {
    var pricingTiers = this.props.pricingTiers || [];

    var rowMatch = seatId.match(/^(\d+)/);
    if (!rowMatch) return 0;

    var rowNumber = parseInt(rowMatch[1], 10);

    for (var i = 0; i < pricingTiers.length; i++) {
      var tier = pricingTiers[i];
      if (tier.rows && tier.rows.indexOf(rowNumber) !== -1) {
        return tier.additionalPrice || 0;
      }
    }

    return 0;
  }

  renderCell(cell) {
    if (cell.type === 'empty') {
      return <div key={cell.id} className="seat-placeholder" aria-hidden="true" />;
    }
    if (cell.type === 'restroom') {
      return (
        <div
          key={cell.id}
          className="seat-restroom"
          title="Restroom"
          role="img"
          aria-label="Restroom"
        >
          <img className="seat-restroom-icon" src={toiletIcon} alt="" aria-hidden="true" />
        </div>
      );
    }
    return this.renderSeat(cell.id);
  }

  renderSeat(seatId) {
    var onSeatSelect = this.props.onSeatSelect;
    var maxSeats = this.props.maxSeats;
    var selectedSeats = this.props.selectedSeats || [];
    var pendingSeats = this.props.pendingSeats || [];
    var pendingReleaseSeats = this.props.pendingReleaseSeats || [];
    var showSeatId = this.props.showSeatId;

    var status = this.getSeatStatus(seatId);
    var price = this.getSeatPrice(seatId);
    var pending = pendingSeats.indexOf(seatId) !== -1;
    var pendingRelease = pendingReleaseSeats.indexOf(seatId) !== -1;

    // Disable selection if max seats reached and seat is not already selected
    var disabled = selectedSeats.length >= maxSeats && status !== SEAT_STATUS.SELECTED;

    return (
      <Seat
        key={seatId}
        seatId={seatId}
        status={status}
        price={price}
        pending={pending}
        pendingRelease={pendingRelease}
        label={showSeatId ? seatId : undefined}
        disabled={disabled}
        onClick={onSeatSelect}
      />
    );
  }

  renderColumnHeaders() {
    var busLayout = this.props.busLayout;
    
    if (!busLayout || !busLayout.columns) {
      return null;
    }

    var seatsPerRowLeft = busLayout.seatsPerRowLeft;
    var seatsPerRowRight = busLayout.seatsPerRowRight;
    var columns = busLayout.columns;
    var leftColumns = columns.slice(0, seatsPerRowLeft);
    var rightColumns = columns.slice(seatsPerRowLeft, seatsPerRowLeft + seatsPerRowRight);
    var isSingleRight = seatsPerRowRight === 1;

    return (
      <div className="seat-row column-headers">
        <div className="left-seats">
          {leftColumns.map(function(col) {
            return <div key={col} className="column-header">{col}</div>;
          })}
        </div>
        <div className="aisle"></div>
        <div className={'right-seats' + (isSingleRight ? ' single' : '')}>
          {rightColumns.map(function(col) {
            return <div key={col} className="column-header">{col}</div>;
          })}
        </div>
      </div>
    );
  }

  renderRow(row) {
    var busLayout = this.props.busLayout;
    var isSingleRight = busLayout && busLayout.seatsPerRowRight === 1;
    var self = this;

    return (
      <div key={row.rowNumber} className="seat-row">
        <div className="left-seats">
          {row.leftCells.map(function(cell) { return self.renderCell(cell); })}
        </div>
        <div className="row-number">{row.rowNumber}</div>
        <div className={'right-seats' + (isSingleRight ? ' single' : '')}>
          {row.rightCells.map(function(cell) { return self.renderCell(cell); })}
        </div>
      </div>
    );
  }

  render() {
    var busLayout = this.props.busLayout;
    
    if (!busLayout || !busLayout.columns || !busLayout.totalRows) {
      return (
        <div className="seat-grid">
          <p>Loading seat layout...</p>
        </div>
      );
    }

    var busType = (busLayout.type || 'standard').toLowerCase();
    var rows = this.generateRows();
    var self = this;

    return (
      <div className={'seat-grid ' + busType}>
        {this.renderColumnHeaders()}
        <div className="seat-rows">
          {rows.map(function(row) { return self.renderRow(row); })}
        </div>
      </div>
    );
  }
}

export default SeatMap;
